// Canonical owner slug for a project — org-owned projects carry
// `{ type: 'organization', slug }`, user-owned carry `{ username }`.
export const ownerSlug = (project) => {
  const o = project?.owner
  if (!o) return project?.ownerUsername || ''
  if (typeof o === 'string') return o
  return o.slug || o.username || ''
}

export const ownerMatches = (project, slug) => ownerSlug(project) === slug

// Strip the legacy `.symbo.ls` / `.tld` suffix from a project key.
export const projectKey = (project) => (project?.key || '').split('.')[0]

// Screenshot CDN (see ~/www/next/SCREENSHOTS.md):
//   https://screenshots.symbo.ls/{owner}/{key}/{page}.webp?env={env}&w={w}
// Exported so hot paths like `normalizeProject` can skip the call
// entirely while the CDN is offline.
//
// `page` defaults to 'root' (maps to the project's `/` route). Pass a
// page slug (e.g. 'pricing', 'about') to get a per-page screenshot URL.
// The CDN worker stores screenshots at scr/{owner}/{key}/{page}/{env}.png
// so any page slug that has been captured is directly addressable.
export const SCREENSHOT_CDN_READY = true
export const projectScreenshotUrl = (project, { page = 'root', env = 'production' } = {}) => {
  if (!SCREENSHOT_CDN_READY) return null
  const owner = ownerSlug(project)
  const key = projectKey(project)
  if (!owner || !key) return null
  // page slug must be a non-empty string; normalize slashes to hyphens so
  // paths like '/pricing' map to the URL segment 'pricing' correctly.
  const pageSlug = (page || 'root').replace(/^\/+/, '').replace(/\//g, '-') || 'root'
  const qs = env && env !== 'production' ? `?env=${encodeURIComponent(env)}` : ''
  return `https://screenshots.symbo.ls/${owner}/${key}/${pageSlug}.webp${qs}`
}

// Group projects by workspace (owner slug). Returns an array of
// `{ owner, items }` pairs preserving insertion order.
export const groupByOwner = (projects) => {
  const map = new Map()
  for (const p of projects) {
    const slug = ownerSlug(p) || 'unknown'
    if (!map.has(slug)) map.set(slug, [])
    map.get(slug).push(p)
  }
  return [...map.entries()].map(([owner, items]) => ({ owner, items }))
}
